<template>
  <div class="terminal-selector">
    <el-dialog
      title="选择接收终端"
      :visible.sync="dialogVisible"
      width="400px"
      @close="handleClose"
    >
      <div class="terminal-list">
        <div
          v-for="terminal in availableTerminals"
          :key="terminal.type"
          class="terminal-item"
          :class="{ 
            disabled: !terminal.online || terminal.type === currentTerminal,
            current: terminal.type === currentTerminal 
          }"
          @click="selectTerminal(terminal)"
        >
          <div class="terminal-icon">{{ terminal.icon }}</div>
          <div class="terminal-info">
            <div class="terminal-name">{{ terminal.name }}</div>
            <div class="terminal-status" :class="getStatusClass(terminal)">
              {{ getStatusText(terminal) }}
            </div>
          </div>
          <div v-if="terminal.type === currentTerminal" class="current-badge">当前</div>
        </div>
      </div>

      <div v-if="onlineTerminals.length === 0" class="empty-state">
        <div class="empty-icon">📱</div>
        <div class="empty-text">暂无其他在线终端</div>
        <div class="empty-hint">请在其他设备上登录后再试</div>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button @click="handleClose">取消</el-button>
        <el-button type="primary" @click="openQuickSend" :disabled="onlineTerminals.length === 0">
          快速发送消息
        </el-button>
      </span>
    </el-dialog>

    <!-- 快速发送消息对话框 -->
    <el-dialog
      title="发送消息到终端"
      :visible.sync="showQuickSend"
      width="500px"
      @close="resetQuickSend"
    >
      <div v-if="selectedTerminal" class="quick-send-header">
        <span class="target-terminal">
          {{ selectedTerminal.icon }} {{ selectedTerminal.name }}
        </span>
      </div>
      
      <el-input
        v-model="quickMessage"
        type="textarea"
        :rows="4"
        placeholder="输入要发送的消息..."
        maxlength="500"
        show-word-limit
        @keydown.ctrl.enter="sendQuickMessage"
      />
      
      <div class="quick-send-tips">
        <span class="tip">💡 按 Ctrl+Enter 快速发送</span>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button @click="resetQuickSend">取消</el-button>
        <el-button 
          type="primary" 
          @click="sendQuickMessage"
          :loading="sending"
          :disabled="!quickMessage.trim()"
        >
          发送
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'TerminalSelector',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dialogVisible: this.visible,
      currentTerminal: this.getCurrentTerminal(),
      availableTerminals: [
        { type: 0, name: '傻蛋网页端', icon: '🌐', online: false },
        { type: 1, name: '傻蛋精灵App', icon: '📱', online: false },
        { type: 2, name: '我的电脑', icon: '💻', online: false },
        { type: 3, name: '我的云电脑', icon: '🖥', online: false },
        { type: 4, name: '傻蛋浏览器', icon: '🔌', online: false },
        { type: 5, name: 'MCP端', icon: '🤖', online: false },
        { type: 6, name: '我的小龙虾', icon: '🦞', online: false }
      ],
      onlineTerminals: [],
      
      // 快速发送相关
      showQuickSend: false,
      selectedTerminal: null,
      quickMessage: '',
      sending: false
    };
  },
  watch: {
    visible(val) {
      this.dialogVisible = val;
      if (val) {
        this.loadTerminalStatus();
      }
    },
    dialogVisible(val) {
      this.$emit('update:visible', val);
    }
  },
  methods: {
    async loadTerminalStatus() {
      try {
        const response = await this.$http.get('/message/private/terminal-status');
        
        // 前端HTTP库直接返回了data部分，所以从response中直接获取onlineTerminals
        console.log('终端在线状态返回值:', response);
        const onlineTerminals = response.otherOnlineTerminals || [];
        
        this.onlineTerminals = onlineTerminals;
        
        this.availableTerminals.forEach(terminal => {
          terminal.online = onlineTerminals.includes(terminal.type);
        });
        
        console.log('🔧 终端在线状态:', {
          current: this.currentTerminal,
          online: onlineTerminals,
          available: this.availableTerminals
        });
      } catch (error) {
        console.error('获取终端状态失败:', error);
        this.$message.error('获取终端状态失败');
      }
    },

    selectTerminal(terminal) {
      if (!terminal.online) {
        this.$message.warning('目标终端不在线');
        return;
      }

      if (terminal.type === this.currentTerminal) {
        this.$message.warning('无法选择当前终端');
        return;
      }

      // 发出终端选择事件
      this.$emit('terminal-selected', terminal.type);
      this.handleClose();
      
      console.log('🎯 选择终端:', terminal);
    },

    openQuickSend() {
      if (this.onlineTerminals.length === 0) {
        this.$message.warning('暂无其他在线终端');
        return;
      }
      
      // 选择第一个在线的非当前终端
      const firstOnlineTerminal = this.availableTerminals.find(
        t => t.online && t.type !== this.currentTerminal
      );
      
      if (firstOnlineTerminal) {
        this.selectedTerminal = firstOnlineTerminal;
        this.showQuickSend = true;
        this.dialogVisible = false;
      }
    },

    async sendQuickMessage() {
      if (!this.quickMessage.trim()) {
        this.$message.warning('请输入消息内容');
        return;
      }
      
      if (!this.selectedTerminal) {
        this.$message.warning('请选择目标终端');
        return;
      }

      this.sending = true;
      
      try {
        const messageData = {
          recvId: this.$store.getters.currentUser.id,
          content: this.quickMessage.trim(),
          type: 0, // 文本消息
          targetTerminal: this.selectedTerminal.type,
          senderTerminal: this.currentTerminal
        };

        const response = await this.$http.post('/message/private/send-to-terminal', messageData);
        
        this.createTargetTerminalChat(this.selectedTerminal.type);
        
        this.$message.success(`消息已发送到${this.selectedTerminal.name}`);
        this.resetQuickSend();
        
        console.log('📤 终端间消息发送成功:', response.data);
      } catch (error) {
        console.error('发送终端间消息失败:', error);
        this.$message.error('发送失败: ' + (error.response?.data?.message || error.message));
      } finally {
        this.sending = false;
      }
    },

    createTargetTerminalChat(targetTerminal) {
      const currentUserId = this.$store.getters.currentUser.id;
      const chatInfo = {
        type: 'PRIVATE',
        targetId: `${currentUserId}_${targetTerminal}`,
        senderTerminal: this.currentTerminal,
        targetTerminal: targetTerminal,
        showName: `${this.getTerminalName(targetTerminal)}端`,
        headImage: this.getTerminalIcon(targetTerminal),
        isTerminalChat: true
      };

      this.$store.commit('openChat', chatInfo);
      
      if (this.$route.name !== 'Chat') {
        this.$router.push('/chat');
      }
    },

    getCurrentTerminal() {
      // 根据环境判断当前终端类型
      if (typeof window !== 'undefined') {
        // 检测VSCode环境 - 多种方式检测
        if (window.parent !== window || // 在iframe中
            window.acquireVsCodeApi || // VSCode WebView API
            window.SharedServicesAccessor || // 我们的VSCode服务
            navigator.userAgent.includes('VSCode') || // User Agent
            window.location.protocol === 'vscode-webview:') { // VSCode WebView协议
          return 2; // VSCode
        }
        
        // 检测Electron环境
        if (window.require || 
            window.process?.type || 
            window.isElectron ||
            navigator.userAgent.includes('Electron')) {
          return 3; // Server
        }
        
        // 检测移动端App环境
        if (window.cordova || 
            window.PhoneGap || 
            window.phonegap ||
            navigator.userAgent.includes('wv') || // WebView
            navigator.userAgent.includes('Mobile')) {
          return 1; // App
        }
        
        return 0; // Web
      }
      return 0; // 默认Web
    },

    getStatusClass(terminal) {
      if (terminal.type === this.currentTerminal) return 'current';
      return terminal.online ? 'online' : 'offline';
    },

    getStatusText(terminal) {
      if (terminal.type === this.currentTerminal) return '当前终端';
      return terminal.online ? '在线' : '离线';
    },

    getTerminalName(terminal) {
      const terminalNames = {
        0: '傻蛋网页端',
        1: '傻蛋精灵App', 
        2: '我的电脑',
        3: '我的云电脑',
        4: '傻蛋浏览器',
        5: 'MCP端',
        6: '我的小龙虾'
      };
      return terminalNames[terminal] || '未知';
    },

    getTerminalIcon(terminal) {
      const terminalIcons = {
        0: '🌐',  // 傻蛋网页端
        1: '📱',  // 傻蛋精灵App
        2: '💻',  // 我的电脑  
        3: '🖥',   // 我的云电脑
        4: '🔌',  // 傻蛋浏览器
        5: '🤖',   // MCP端
        6: '🦞'   // 我的小龙虾
      };
      return terminalIcons[terminal] || '❓';
    },

    handleClose() {
      this.dialogVisible = false;
      this.resetQuickSend();
    },

    resetQuickSend() {
      this.showQuickSend = false;
      this.selectedTerminal = null;
      this.quickMessage = '';
      this.sending = false;
    }
  }
};
</script>

<style scoped>
.terminal-list {
  max-height: 300px;
  overflow-y: auto;
}

.terminal-item {
  display: flex;
  align-items: center;
  padding: 12px;
  margin-bottom: 8px;
  border: 1px solid #e1e6eb;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s;
  position: relative;
}

.terminal-item:hover:not(.disabled) {
  background-color: #f0f9ff;
  border-color: #3b82f6;
}

.terminal-item.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.terminal-item.current {
  background-color: #fff7ed;
  border-color: #f59e0b;
}

.terminal-icon {
  font-size: 24px;
  margin-right: 12px;
}

.terminal-info {
  flex: 1;
}

.terminal-name {
  font-weight: 500;
  color: #1f2937;
}

.terminal-status {
  font-size: 12px;
  margin-top: 2px;
}

.terminal-status.online {
  color: #10b981;
}

.terminal-status.offline {
  color: #ef4444;
}

.terminal-status.current {
  color: #f59e0b;
}

.current-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  padding: 2px 6px;
  font-size: 10px;
  background-color: #f59e0b;
  color: white;
  border-radius: 10px;
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #909399;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.empty-text {
  font-size: 16px;
  margin-bottom: 8px;
}

.empty-hint {
  font-size: 12px;
  color: #c0c4cc;
}

.quick-send-header {
  margin-bottom: 16px;
  padding: 12px;
  background-color: #f5f7fa;
  border-radius: 4px;
}

.target-terminal {
  font-weight: 500;
  color: #409eff;
}

.quick-send-tips {
  margin-top: 8px;
  font-size: 12px;
  color: #909399;
}

.tip {
  display: flex;
  align-items: center;
}
</style>
